import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { field } from "@/pages/Auth";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const small = "rounded-full border border-[#8B2CFF] px-3 py-1.5 text-sm text-[#EDE6F7] hover:bg-[#241634] whitespace-nowrap";
const ACTIONS = {
  disable: { label: "Disable access", ask: "Disable this user? They'll be signed out straight away and can't log in." },
  enable: { label: "Enable access", ask: "Enable this user again?" },
  "revoke-sessions": { label: "Sign out everywhere", ask: "Sign this user out on all devices?" },
  "send-password-link": { label: "Send password link", ask: "Email this user a secure link to set a new password? You won't see the link or their password." },
};

const actsFor = (u) => (u.is_owner ? [] : u.status === "disabled" ? ["enable"] : ["disable", "revoke-sessions", "send-password-link"]);
const actLabel = (u, a) => (u.status === "invited" && a === "send-password-link" ? "Resend invite" : ACTIONS[a].label);

function UserCard({ u, onAction }) {
  return (
    <div className="rounded-2xl border border-[#33264A] bg-[#1A1222] p-4 space-y-2" data-testid={`admin-user-card-${u.email}`}>
      <p className="text-[#EDE6F7] font-semibold">{u.name} <span className="font-normal text-[#A49CB8]">· {u.role}</span></p>
      <p className="text-sm text-[#A49CB8] break-all">{u.email}</p>
      <p className="text-sm text-[#C9C2D8]">Status: {u.status} · Two-step: {u.mfa_enabled ? "On" : "Not set up"}</p>
      <p className="text-sm text-[#C9C2D8]">Last login: {when(u.last_login_at)}</p>
      <div className="flex flex-wrap gap-2 pt-1">{actsFor(u).map((a) => (
        <button key={a} type="button" className={small} onClick={() => onAction(u, a)} data-testid={`admin-user-m-${a}-${u.email}`}>{actLabel(u, a)}</button>
      ))}</div>
    </div>
  );
}

function UserRow({ u, onAction }) {
  const acts = actsFor(u);
  return (
    <tr className="border-t border-[#2E2140] align-top" data-testid={`admin-user-row-${u.email}`}>
      <td className="py-3 pr-4"><span className="block text-[#EDE6F7] font-semibold">{u.name}</span><span className="text-[#A49CB8]">{u.email}</span></td>
      <td className="pr-4">{u.role}</td>
      <td className="pr-4" data-testid={`admin-user-status-${u.email}`}>{u.status}</td>
      <td className="pr-4">{u.mfa_enabled ? "On" : "Not set up"}</td>
      <td className="pr-4">{when(u.last_login_at)}</td>
      <td className="py-3"><div className="flex flex-wrap gap-2">{acts.map((a) => (
        <button key={a} type="button" className={small} onClick={() => onAction(u, a)} data-testid={`admin-user-${a}-${u.email}`}>{actLabel(u, a)}</button>
      ))}</div></td>
    </tr>
  );
}

function InviteForm({ onInvited }) {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/users/invite", f); toast.success(`Invitation sent to ${f.email}`); setF({ name: "", email: "" }); onInvited(); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} noValidate className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end" data-testid="admin-invite-form">
      <label className="block"><span className="block text-sm text-[#C9C2D8] mb-1">Name</span><input className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-invite-name" /></label>
      <label className="block"><span className="block text-sm text-[#C9C2D8] mb-1">Email</span><input type="email" className={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-invite-email" /></label>
      <button type="submit" disabled={busy} className="gold-btn h-14 rounded-full px-6 disabled:opacity-60" data-testid="admin-invite-submit">{busy ? "Sending…" : "Send invite"}</button>
    </form>
  );
}

function AuditLog({ entries: all }) {
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();
  const entries = t ? all.filter((e) => [e.action, e.actor_email, e.target_email].some((v) => (v || "").toLowerCase().includes(t))) : all;
  return (
    <div className="overflow-x-auto space-y-3" data-testid="admin-audit-log">
      <input placeholder="Filter by email or action" className={`${field} max-w-sm`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-audit-filter" />
      <table className="w-full text-sm text-left text-[#C9C2D8]">
        <thead className="text-[#A49CB8]"><tr><th className="py-2 pr-4">When</th><th className="pr-4">Action</th><th className="pr-4">By</th><th className="pr-4">Affected</th><th>IP</th></tr></thead>
        <tbody>{entries.map((e) => (
          <tr key={e.id} className="border-t border-[#2E2140]"><td className="py-2 pr-4 whitespace-nowrap">{when(e.at)}</td><td className="pr-4">{e.action.replaceAll("_", " ")}</td>
            <td className="pr-4">{e.actor_email || "—"}</td><td className="pr-4">{e.target_email || "—"}</td><td>{e.ip || "—"}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState("");
  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch((e) => setError(apiError(e)));
    api.get("/admin/audit").then((r) => setAudit(r.data.entries)).catch(() => {});
  };
  useEffect(load, []);
  const onAction = async (u, a) => {
    if (!window.confirm(ACTIONS[a].ask)) return;
    try { await api.post(`/admin/users/${u.id}/${a}`); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="admin-users">
        <div>
          <Link to="/admin" className="text-[#C9B6FF] underline underline-offset-4" data-testid="admin-users-back">← Admin dashboard</Link>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-[#EDE6F7]">Users & access</h1>
          <p className="mt-2 text-[#B9B3C8]">Invited users get read-only access and must set up two-step verification. Passwords are never visible to anyone.</p>
        </div>
        {error && <p className="text-[#E6CCF2]" data-testid="admin-users-error">{error}</p>}
        <section className="glass-card p-6 space-y-4"><h2 className="text-lg text-[#EDE6F7]">Invite a read-only admin</h2><InviteForm onInvited={load} /></section>
        {users && (
          <section className="glass-card p-4 space-y-3 md:hidden" data-testid="admin-users-cards">{users.map((u) => <UserCard key={u.id} u={u} onAction={onAction} />)}</section>
        )}
        {users && (
          <section className="glass-card p-6 overflow-x-auto hidden md:block">
            <table className="w-full text-sm text-left text-[#C9C2D8]">
              <thead className="text-[#A49CB8]"><tr><th className="py-2 pr-4">User</th><th className="pr-4">Role</th><th className="pr-4">Status</th><th className="pr-4">Two-step</th><th className="pr-4">Last login</th><th>Actions</th></tr></thead>
              <tbody>{users.map((u) => <UserRow key={u.id} u={u} onAction={onAction} />)}</tbody>
            </table>
          </section>
        )}
        <section className="glass-card p-6 space-y-3"><h2 className="text-lg text-[#EDE6F7]">Audit log</h2><AuditLog entries={audit} /></section>
      </main>
    </div>
  );
}
